import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import Loading from "../Loading";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    SelectionCard,
    Wrapper,
    ActionSection,
    ScrollableContent,
    editOptionLoading,
    editOptionMutate,
    RupeeIcon,
    ModalHeader,
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import Config, { availableAmenities } from "../../Config";


const EditPackage = ({ handleClose, packageData, editOptionMutate, editOptionLoading }) => {

    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight); // updates when Safari UI changes
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const initialValues = {
        title: packageData?.name || '',
        amenities: packageData?.amenities
            ? packageData.amenities.split(",").map(amenity => {
                const matchedAmenity = availableAmenities.find(item => item.value === amenity.trim());
                return matchedAmenity ? matchedAmenity.value : null;
            }).filter(Boolean)
            : [],
        rent: packageData?.rent || '',
        roomOptionId: packageData?.id || '',
        security: packageData?.security || "",
    };

    const handleSubmit = (values) => {
        const amountRegex = /^(?!0+$)\d{4,6}$/;
        const maxAmount = 1000000;
        const titleRegex = /^(?=.*[A-Za-z0-9])[A-Za-z0-9 ]{5,}$/;
        const maxSecurityAmount = 1000000;

        if (!values.title || values.amenities.length === 0) {
            toast.error("All Fields are required");
            return;
        }
        if (!titleRegex.test(values.title)) {
            toast.error(
                "Title must be at least 5 characters long and can contain alphabets and numbers"
            );
            return;
        }
        if (values.security && values.security !== "") {
            const securityAmount = parseInt(values.security);
            if (isNaN(securityAmount) || securityAmount <= 0) {
                toast.error("Security deposit must be a positive number");
                return;
            }
            if (securityAmount > maxSecurityAmount) {
                toast.error(`Security deposit cannot exceed ₹${maxSecurityAmount.toLocaleString()}`);
                return;
            }
        }
        if (!amountRegex.test(values.rent) || parseInt(values.rent) > maxAmount) {
            toast.error(
                "Monthly rent must be between 1000 and 10,00,000 and contain only numbers"
            );
            return;
        }

        editOptionMutate({
            name: values.title,
            rent: values.rent,
            amenities: values.amenities.join(","),
            roomOptionId: packageData.id,
            security: values.security || 0,
        });

    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Edit Option</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
                    {({ values, setFieldValue }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100dvh-120px)]">
                                <Wrapper>
                                    <InputGroup>
                                        <Label htmlFor="title">Title *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="title"
                                                id="title"
                                                autoComplete="off"
                                                required
                                            />
                                        </FieldWrappers>
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="rent">Room Rent *</Label>
                                        <FieldWrappers>
                                            <RupeeIcon />
                                            <Field
                                                type="number"
                                                name="rent"
                                                id="rent"
                                                autoComplete="off"
                                                required
                                                className="w-full truncate pl-6"
                                            />
                                        </FieldWrappers>
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="security">Security </Label>
                                        <FieldWrappers>
                                            <RupeeIcon />
                                            <Field
                                                type="number"
                                                name="security"
                                                id="security"
                                                autoComplete="off"
                                                className="w-full truncate pl-6"
                                            />
                                        </FieldWrappers>
                                    </InputGroup>

                                    <InputGroup>
                                        <Label>Room Amenities *</Label>
                                        <CardWrapper>
                                            {availableAmenities.map((mode) => (
                                                <SelectionCard
                                                    key={mode.value}
                                                    type="button"
                                                    onClick={() => {
                                                        const isSelected = values.amenities.includes(
                                                            mode.value
                                                        );
                                                        if (isSelected) {
                                                            setFieldValue(
                                                                "amenities",
                                                                values.amenities.filter(
                                                                    (item) => item !== mode.value
                                                                )
                                                            );
                                                        } else {
                                                            setFieldValue("amenities", [
                                                                ...values.amenities,
                                                                mode.value,
                                                            ]);
                                                        }
                                                    }}
                                                    className={
                                                        values.amenities.includes(mode.value)
                                                            ? "bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 text-white"
                                                            : "bg-gray-100 text-gray-600"
                                                    }
                                                >
                                                    <span className="text-sm">{mode.label}</span>
                                                </SelectionCard>
                                            ))}
                                        </CardWrapper>
                                    </InputGroup>
                                </Wrapper>
                            </ScrollableContent>
                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn type="submit" disabled={editOptionLoading}>{editOptionLoading ? "Submitting..." : "Submit"}</SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const CardWrapper = tw.div`flex flex-wrap gap-2 my-1.5`;
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] py-4 px-4`;
export default EditPackage;
